import { ChevronDownIcon, ChevronUpIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Collapse,
  HStack,
  IconButton,
  Input,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';

import Text from 'components/Text';

import DevSandboxTooltip from './DevSandboxTooltip';
import useSandboxTheme from './useSandboxTheme';

interface ConsoleEntry {
  method: 'log' | 'warn' | 'error' | 'info' | 'input';
  args: string[];
  timestamp: number;
}

const MAX_ENTRIES = 200;

// Log method colors work well in both dark and light — warn/error/info use semantic colors
const methodColors: Record<string, { dark: string; light: string }> = {
  log: { dark: 'whiteAlpha.900', light: 'gray.800' },
  warn: { dark: 'yellow.300', light: 'yellow.700' },
  error: { dark: 'red.300', light: 'red.600' },
  info: { dark: 'blue.300', light: 'blue.600' },
  input: { dark: 'purple.300', light: 'purple.600' },
};

const methodPrefixes: Record<string, string> = {
  log: '',
  warn: '[warn]',
  error: '[error]',
  info: '[info]',
  input: '>',
};

interface IConsolePanelProps {
  isOpen: boolean;
  onToggle: () => void;
}

/**
 * Console panel that pops up from footer.
 * Shows console output from iframe and allows command input.
 */
const ConsolePanel = ({ isOpen, onToggle }: IConsolePanelProps) => {
  const [entries, setEntries] = useState<ConsoleEntry[]>([]);
  const [inputValue, setInputValue] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { colors, resolved } = useSandboxTheme();

  // Listen for console messages from iframe
  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === 'bds-console') {
        const { method, args } = event.data;
        setEntries((prev) => {
          const newEntry: ConsoleEntry = {
            method,
            args,
            timestamp: Date.now(),
          };
          return [...prev, newEntry].slice(-MAX_ENTRIES);
        });
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Auto-scroll to bottom when new entries added
  useEffect(() => {
    if (scrollRef.current && isOpen) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [entries, isOpen]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const clearConsole = () => setEntries([]);

  const handleInputSubmit = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      // Add input to console
      setEntries((prev) => [
        ...prev,
        { method: 'input', args: [inputValue], timestamp: Date.now() },
      ]);

      // Execute in iframe context
      try {
        const iframe = document.querySelector('.bds-code-frame') as HTMLIFrameElement;
        if (iframe?.contentWindow) {
          // Send eval request to iframe
          iframe.contentWindow.postMessage({ type: 'bds-eval', code: inputValue }, '*');
        }
      } catch {
        setEntries((prev) => [
          ...prev,
          { method: 'error', args: ['Failed to execute command'], timestamp: Date.now() },
        ]);
      }

      setInputValue('');
    }
  };

  const errorCount = entries.filter((e) => e.method === 'error').length;
  const warnCount = entries.filter((e) => e.method === 'warn').length;

  const getMethodColor = (method: string) => methodColors[method]?.[resolved] || colors.textPrimary;

  return (
    <Box
      position="absolute"
      bottom="60px"
      left={0}
      right={0}
      zIndex={1000}
      pointerEvents={isOpen ? 'auto' : 'none'}
    >
      <Collapse in={isOpen} animateOpacity>
        <Box
          bg={colors.consoleBg}
          borderTop="1px solid"
          borderColor={colors.consoleBorder}
          maxH="250px"
          display="flex"
          flexDirection="column"
        >
          {/* Header */}
          <HStack
            px={3}
            py={2}
            borderBottom="1px solid"
            borderColor={colors.editorDivider}
            justify="space-between"
          >
            <HStack spacing={3}>
              <Text fontSize="small" fontWeight="medium">
                Console
              </Text>
              {errorCount > 0 && (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="red.400" />
                  <ChakraText fontSize="xs" color={getMethodColor('error')}>
                    {errorCount}
                  </ChakraText>
                </HStack>
              )}
              {warnCount > 0 && (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="yellow.400" />
                  <ChakraText fontSize="xs" color={getMethodColor('warn')}>
                    {warnCount}
                  </ChakraText>
                </HStack>
              )}
            </HStack>
            <HStack spacing={1}>
              <DevSandboxTooltip label="Clear console">
                <IconButton
                  aria-label="Clear console"
                  icon={<DeleteIcon boxSize={3} />}
                  size="xs"
                  variant="ghost"
                  onClick={clearConsole}
                />
              </DevSandboxTooltip>
              <DevSandboxTooltip label="Close console">
                <IconButton
                  aria-label="Close console"
                  icon={<ChevronDownIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={onToggle}
                />
              </DevSandboxTooltip>
            </HStack>
          </HStack>

          {/* Log entries */}
          <Box ref={scrollRef} flex={1} overflowY="auto" maxH="160px">
            {entries.length === 0 ? (
              <Box px={3} py={4}>
                <ChakraText fontSize="xs" color={colors.textMuted}>
                  Console output will appear here...
                </ChakraText>
              </Box>
            ) : (
              <VStack align="stretch" spacing={0}>
                {entries.map((entry, i) => (
                  <Box
                    key={`${entry.timestamp}-${i}`}
                    px={3}
                    py={1}
                    fontFamily="mono"
                    fontSize="xs"
                    bg={entry.method === 'error' ? 'rgba(255,0,0,0.08)' : 'transparent'}
                    borderBottom="1px solid"
                    borderColor={colors.editorDivider}
                    _hover={{ bg: colors.hoverBg }}
                  >
                    <ChakraText color={getMethodColor(entry.method)} whiteSpace="pre-wrap">
                      {methodPrefixes[entry.method] && (
                        <ChakraText as="span" color={getMethodColor(entry.method)} opacity={0.7} mr={2}>
                          {methodPrefixes[entry.method]}
                        </ChakraText>
                      )}
                      {entry.args.join(' ')}
                    </ChakraText>
                  </Box>
                ))}
              </VStack>
            )}
          </Box>

          {/* Input */}
          <Box px={3} py={2} borderTop="1px solid" borderColor={colors.editorDivider}>
            <HStack spacing={2}>
              <ChakraText color="purple.300" fontSize="xs" fontFamily="mono">
                &gt;
              </ChakraText>
              <Input
                ref={inputRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleInputSubmit}
                placeholder="Type JavaScript and press Enter..."
                size="xs"
                variant="unstyled"
                fontFamily="mono"
                fontSize="xs"
                color={colors.textPrimary}
                _placeholder={{ color: colors.textMuted }}
              />
            </HStack>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

// Terminal icon as inline SVG
const TerminalIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <polyline points="6,9 10,12 6,15" strokeLinecap="round" strokeLinejoin="round" />
    <line x1="12" y1="15" x2="18" y2="15" strokeLinecap="round" />
  </svg>
);

export const ConsoleButton = ({ onClick, isActive }: { onClick: () => void; isActive?: boolean }) => {
  const { colors } = useSandboxTheme();

  return (
    <Button
      aria-label="Toggle console"
      leftIcon={<Box as="span" display="flex"><TerminalIcon /></Box>}
      size="xs"
      variant="ghost"
      color={isActive ? colors.textSecondary : colors.textMuted}
      bg={isActive ? colors.controlBg : 'transparent'}
      h="28px"
      fontSize="11px"
      fontWeight="500"
      _hover={{ color: colors.textSecondary, bg: colors.controlBg }}
      _active={{ bg: colors.activeBg }}
      onClick={onClick}
    >
      Console
    </Button>
  );
};

export default ConsolePanel;
